import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function DemoSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'notebook' | 'performance'>('notebook');

  return (
    <section id="demo-section" className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              See It In Action
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Watch how PropAlgos accelerates your AI-powered trading strategy development with GPU acceleration and instant data access.
            </p>
          </div>

          {/* Demo Tabs */}
          <div className="flex justify-center mb-8">
            <div className="flex bg-gray-900 rounded-lg p-1 border border-gray-800">
              <button
                onClick={() => setActiveTab('notebook')}
                className={`px-6 py-3 rounded-md font-medium transition-all ${
                  activeTab === 'notebook'
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Notebook Preview
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`px-6 py-3 rounded-md font-medium transition-all ${
                  activeTab === 'performance'
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                GPU Performance
              </button>
            </div>
          </div>

          {/* Demo Content */}
          <div className="relative">
            {activeTab === 'notebook' && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-8">
                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <div className="flex space-x-2 mr-4">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-400 text-sm">strategy_backtest.ipynb</span>
                  </div>
                  
                  {/* Notebook Cell */}
                  <div className="bg-black/50 rounded-lg p-4 mb-4">
                    <div className="text-gray-500 text-sm mb-2">In [1]:</div>
                    <div className="font-mono text-sm text-left">
                      <div><span className="text-purple-400">import</span><span className="text-white"> pandas </span><span className="text-purple-400">as</span><span className="text-white"> pd</span></div>
                      <div><span className="text-purple-400">import</span><span className="text-white"> numpy </span><span className="text-purple-400">as</span><span className="text-white"> np</span></div>
                      <div><span className="text-purple-400">from</span><span className="text-white"> propalgos </span><span className="text-purple-400">import</span><span className="text-white"> load_data, gpu_backtest</span></div>
                      <br />
                      <div className="text-gray-500"># Load pre-loaded S&P 500 data</div>
                      <div><span className="text-white">data = load_data(</span><span className="text-green-400">'SPY'</span><span className="text-white">, years=</span><span className="text-yellow-400">5</span><span className="text-white">)</span></div>
                      <div><span className="text-white">print(f</span><span className="text-green-400">"Loaded {'{len(data)}'} days of data"</span><span className="text-white">)</span></div>
                    </div>
                  </div>

                  {/* Output Cell */}
                  <div className="bg-black/50 rounded-lg p-4">
                    <div className="text-gray-500 text-sm mb-2">Out [1]:</div>
                    <div className="font-mono text-sm text-white text-left">
                      Loaded 1,258 days of data
                    </div>
                  </div>
                </div>

                {/* Strategy Cell */}
                <div className="bg-black/50 rounded-lg p-4 mb-4">
                  <div className="text-gray-500 text-sm mb-2">In [2]:</div>
                  <div className="font-mono text-sm text-left">
                    <div className="text-gray-500"># Define moving average strategy</div>
                    <br />
                    <span className="text-purple-400">def</span>
                    <span className="text-blue-400"> moving_average_strategy</span>
                    <span className="text-white">(data, short_window=</span>
                    <span className="text-yellow-400">20</span>
                    <span className="text-white">, long_window=</span>
                    <span className="text-yellow-400">50</span>
                    <span className="text-white">):</span>
                    <br />
                    <span className="text-white">    signals = data.copy()</span>
                    <br />
                    <span className="text-white">    signals[</span>
                    <span className="text-green-400">'short_ma'</span>
                    <span className="text-white">] = data[</span>
                    <span className="text-green-400">'close'</span>
                    <span className="text-white">].rolling(short_window).mean()</span>
                    <br />
                    <span className="text-white">    signals[</span>
                    <span className="text-green-400">'long_ma'</span>
                    <span className="text-white">] = data[</span>
                    <span className="text-green-400">'close'</span>
                    <span className="text-white">].rolling(long_window).mean()</span>
                    <br />
                    <span className="text-purple-400">    return</span>
                    <span className="text-white"> signals</span>
                    <br />
                    <br />
                    <div className="text-gray-500"># Run GPU-accelerated backtest</div>
                    <br />
                    <span className="text-white">results = gpu_backtest(moving_average_strategy, data)</span>
                    <br />
                    <span className="text-white">print(f</span>
                    <span className="text-green-400">"Sharpe Ratio: {'{results[\'sharpe\']:.2f}'}"</span>
                    <span className="text-white">)</span>
                  </div>
                </div>

                {/* Performance Output */}
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="text-gray-500 text-sm mb-2">Out [2]:</div>
                  <div className="font-mono text-sm text-left">
                    <div className="text-green-400">✓ GPU acceleration enabled</div>
                    <div className="text-white">Backtest completed in 1.2s</div>
                    <div className="text-white">Sharpe Ratio: 1.34</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'performance' && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  
                  {/* CPU vs GPU Comparison */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6">Speed Comparison</h3>
                    
                    <div className="space-y-4">
                      <div className="bg-black/50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300">CPU Processing</span>
                          <span className="text-red-400 font-mono">12.4s</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2">
                          <div className="bg-red-500 h-2 rounded-full w-full"></div>
                        </div>
                      </div>
                      
                      <div className="bg-black/50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-300">GPU Processing</span>
                          <span className="text-green-400 font-mono">1.2s</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full w-1/6"></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-4 bg-green-900/20 border border-green-800/30 rounded-lg">
                      <div className="text-green-400 font-semibold">10.3x faster</div>
                      <div className="text-gray-400 text-sm">with T4 GPU acceleration</div>
                    </div>
                  </div>
                  
                  {/* Cost Breakdown */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6">Cost Breakdown</h3>
                    
                    <div className="space-y-4">
                      <div className="bg-black/50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">GPU Time</span>
                          <span className="text-white font-mono">1.2s</span>
                        </div>
                      </div>
                      
                      <div className="bg-black/50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">Rate</span>
                          <span className="text-white font-mono">$0.50/hour</span>
                        </div>
                      </div>
                      
                      <div className="bg-black/50 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300">Total Cost</span>
                          <span className="text-green-400 font-mono">$0.0002</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-4 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                      <div className="text-blue-400 font-semibold">Transparent pricing</div>
                      <div className="text-gray-400 text-sm">Pay only for what you use</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white hover:bg-gray-100 text-black font-medium rounded-lg transition-all duration-200"
            >
              Try It Yourself
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}